/**
 * MultiplayerPage — Create or join a multiplayer room.
 * Uses socket emitters to create/join rooms on the server.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRoomStore, useAuthStore } from '../stores';
import {
  emitCreateRoom,
  emitJoinRoom,
  emitLeaveRoom,
  emitRejoinRoom,
  RoomConflictError,
} from '../hooks/useSocket';
import {
  SpadeSvg,
  HeartSvg,
  DiamondSvg,
  ClubSvg,
  BackArrowSvg,
  LogoutSvg,
} from '../components/SuitIcons';

type LobbyTab = 'create' | 'join';

export function MultiplayerPage() {
  const [activeTab, setActiveTab] = useState<LobbyTab>('create');
  const [joinCode, setJoinCode] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [existingRoomCode, setExistingRoomCode] = useState<string | null>(null);
  const [leavingRoom, setLeavingRoom] = useState(false);
  const { roomCreating, roomJoining, roomJoined, roomError, errorCleared, isLoading, error } =
    useRoomStore();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();

  async function handleCreate() {
    roomCreating();
    setExistingRoomCode(null);

    try {
      const { roomCode } = await emitCreateRoom(isPrivate, 4);

      // Update room store with initial state
      roomJoined(
        roomCode,
        [
          {
            id: user?.id ?? '',
            name: user?.username ?? 'You',
            isHost: true,
            isReady: false,
            isAI: false,
          },
        ],
        4,
        isPrivate,
        user?.id ?? '',
      );

      navigate(`/game/${roomCode}?mode=lobby&host=true`);
    } catch (err) {
      if (err instanceof RoomConflictError && err.existingRoomCode) {
        setExistingRoomCode(err.existingRoomCode);
      }
      roomError(err instanceof Error ? err.message : 'Failed to create room');
    }
  }

  async function handleJoin() {
    if (!joinCode.trim()) {
      roomError('Please enter a room code');
      return;
    }

    roomJoining();
    setExistingRoomCode(null);

    try {
      await emitJoinRoom(joinCode.toUpperCase());

      // The room:updated event from server will populate the room store
      // For now set minimal state — server will send full update
      roomJoined(joinCode.toUpperCase(), [], 4, false, user?.id ?? '');

      navigate(`/game/${joinCode.toUpperCase()}?mode=lobby`);
    } catch (err) {
      if (err instanceof RoomConflictError && err.existingRoomCode) {
        setExistingRoomCode(err.existingRoomCode);
      }
      roomError(err instanceof Error ? err.message : 'Failed to join room');
    }
  }

  async function handleGoToExistingRoom() {
    if (!existingRoomCode) return;
    try {
      const room = await emitRejoinRoom();
      const isHost = room.players.find((p) => p.id === user?.id)?.isHost ?? false;
      roomJoined(room.roomCode, room.players, room.maxPlayers, room.isPrivate, user?.id ?? '');
      navigate(`/game/${room.roomCode}?mode=lobby${isHost ? '&host=true' : ''}`);
    } catch {
      navigate(`/game/${existingRoomCode}?mode=lobby`);
    }
  }

  async function handleLeaveExistingRoom() {
    setLeavingRoom(true);
    try {
      await emitLeaveRoom();
      setExistingRoomCode(null);
      errorCleared();
    } catch (err) {
      roomError(err instanceof Error ? err.message : 'Failed to leave room');
    } finally {
      setLeavingRoom(false);
    }
  }

  return (
    <div className="page multiplayer-page">
      <div className="page-bg-suits" aria-hidden="true">
        <SpadeSvg className="page-bg-icon page-bg-1" />
        <HeartSvg className="page-bg-icon page-bg-2" />
        <DiamondSvg className="page-bg-icon page-bg-3" />
        <ClubSvg className="page-bg-icon page-bg-4" />
        <SpadeSvg className="page-bg-icon page-bg-5" />
        <HeartSvg className="page-bg-icon page-bg-6" />
      </div>

      <div className="page-topbar">
        <button className="page-back" onClick={() => navigate('/')}>
          <BackArrowSvg />
          Back
        </button>
        <div className="page-topbar-right">
          {user && <span className="page-username">♠ {user.username}</span>}
          <button className="page-logout" onClick={logout}>
            <LogoutSvg />
            Logout
          </button>
        </div>
      </div>

      <div className="setup-hero">
        <div className="setup-hero-icon">
          <ClubSvg className="hero-suit-svg" />
        </div>
        <h1>Multiplayer</h1>
        <p className="setup-subtitle">Play online with friends or other players</p>
      </div>

      <div className="lobby-tabs">
        <button
          className={`tab ${activeTab === 'create' ? 'active' : ''}`}
          onClick={() => setActiveTab('create')}
        >
          Create Room
        </button>
        <button
          className={`tab ${activeTab === 'join' ? 'active' : ''}`}
          onClick={() => setActiveTab('join')}
        >
          Join Room
        </button>
      </div>

      {error && (
        <div className="auth-error" role="alert">
          {error}
          {existingRoomCode && (
            <div className="room-conflict-actions">
              <span className="room-conflict-code">Current room: {existingRoomCode}</span>
              <button className="btn btn-sm btn-outline" onClick={handleGoToExistingRoom}>
                Go to Room
              </button>
              <button
                className="btn btn-sm btn-outline"
                onClick={handleLeaveExistingRoom}
                disabled={leavingRoom}
              >
                {leavingRoom ? 'Leaving...' : 'Leave Room'}
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'create' ? (
        <div className="lobby-panel">
          <p className="lobby-info">
            Create a room and share the code with friends. You can also add AI players.
          </p>

          <div className="settings-toggle-row lobby-toggle-row">
            <div className="toggle-info">
              <span className="toggle-label">Private Room</span>
              <span className="toggle-desc">Only players with the room code can join</span>
            </div>
            <button
              type="button"
              className={`toggle-switch ${isPrivate ? 'active' : ''}`}
              onClick={() => setIsPrivate((prev) => !prev)}
              aria-label="Toggle private room"
              aria-pressed={isPrivate}
            >
              <span className="toggle-knob" />
            </button>
          </div>

          <div className="settings-info-row lobby-info-row">
            <span className="settings-label">Host</span>
            <span className="settings-value">{user?.username ?? 'You'}</span>
          </div>

          <button
            className="btn btn-primary setup-start-btn"
            onClick={handleCreate}
            disabled={isLoading}
          >
            {isLoading ? 'Creating...' : 'Create Room'}
          </button>
        </div>
      ) : (
        <div className="lobby-panel">
          <p className="lobby-info">Enter the room code shared by the host to join their game.</p>

          <div className="form-field">
            <label htmlFor="roomCode">Room Code</label>
            <input
              id="roomCode"
              type="text"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              placeholder="e.g. ABC123"
              maxLength={8}
              className="room-code-input"
              disabled={isLoading}
            />
          </div>

          <button
            className="btn btn-primary setup-start-btn"
            onClick={handleJoin}
            disabled={isLoading || !joinCode.trim()}
          >
            {isLoading ? 'Joining...' : 'Join Room'}
          </button>
        </div>
      )}
    </div>
  );
}
